import { animate, style, transition, trigger } from '@angular/animations';
import { Component, ElementRef, HostListener, OnInit, ViewChild, OnDestroy  } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from 'src/app/services/auth.service';
import { DataService } from 'src/app/services/data.service';

export interface ChangeLog {
  id: number;
  title: string;
  description: string;
  what: string;
  why: string;
  how: string;
  date: string;
  expanded: boolean;
  category: string;
  imageLinks: Array<{ link: string, route: any}>;
  order: number;
  sensorDetails?: SensorDetail[];
  itemList?: any;
}

interface SensorDetail {
  sensorType: string;
  resolution: string;
  numberOfBands?: number | string;
  availableBands?: string[];
  taskingTiers?: string[];
  modes?: string[];
  frequency?: string;
  polarisation?: string;
  screenshot: string;
  numberOfModes?: any;
  route?: string;
  expanded: boolean
}

@Component({
  selector: 'app-whats-new',
  templateUrl: './whats-new.component.html',
  styleUrls: ['./whats-new.component.scss'],
  animations: [
    trigger('expandCollapse', [
      transition(':enter', [
        style({ height: 0, opacity: 0 }),
        animate('300ms ease-out', style({ height: '*', opacity: 1 }))
      ]),
      transition(':leave', [
        animate('300ms ease-in', style({ height: 0, opacity: 0 }))
      ])
    ])
  ]
})
export class WhatsNewComponent implements OnInit, OnDestroy  {
  features: ChangeLog[];
  activeDate: string = '';
  @ViewChild('timelineContainer') timelineContainer: ElementRef;
  activeIndex = 0;
  categories = [
    {
      featureKey : 'all',
      featureName: 'All Features'
    },
    {
      featureKey : 'platform',
      featureName: 'Platform Features'
    },    {
      featureKey : 'sensors',
      featureName: 'Sensors'
    }
  ];
  selectedIndex = 0;
  filteredFeatures: ChangeLog[];
  darkTheme: boolean;
  currentUser: any;
  excludedIds = new Set([8, 9, 20, 21, 22]);

  constructor(
    private localStorageService: DataService,
    private authService: AuthService,
    private router: Router
  ) {
    const castUser = this.authService.castCurrentUser.subscribe(
      async (res: any) => {        
        this.currentUser = await res;
        if (!this.currentUser?.verified) {
          this.authService.logoutAndReset();
        }
        castUser.unsubscribe();
      }
    );

    this.getFeatures();
    this.localStorageService.darkTheme.subscribe((newValue) => {
      this.darkTheme = newValue;
    });
  }

  getFeatures() {
    this.features = [
      {
        id: 1,
        title: 'Annotation',
        description: 'Make notes on the collection page map.',
        what: `Users can annotate the data delivered on the map with text.`,
        why:`Making notes on objects or features of interest in the tasked data, including imaging and non-imaging data, assists in capturing the user's interpretation of the data for further analysis and collaboration.`,
        how: `Users can right-click on any point in the data and annotate within the pop-up dialogue. When saving the notes made, an icon is placed on the coordinate. Left-clicking the icon brings back the notes for editing or deleting.
         Once annotated, the split screen also shows the notes, comparing an area or point of interest with different sensors or periods.`,
        date: '2024-07-15',
        imageLinks: [
          {link: '../../../assets/feature-images/annotation.png', route: '/collections'},        
        ],
        expanded: false,
        category: 'platform',
        order: 1
      },
      {
        id: 2,
        title: 'Tasking over a point of interest on the data delivered',
        description: 'Task from the collection screen.',
        what: `Users can now task from the map in the collection screen, where the data and the feeds delivered necessitate further investigation.`,
        why:`The data and/or feeds delivered, say vessels detected, may have pointed to a specific vessel of interest. Tasking over the vessel now with another sensor, such as a high-resolution optical or SAR, would give the user further information about the vessel, depending on the weather and time.`,
        how: `Only point tasking is enabled so that users can right-click on a coordinate of interest on the tasked- data delivered. They then provide a minimum set of requirements, and the platform will assign tasks instantly based on availability. The ordered task cannot be canceled because it is submitted for immediate tasking.`,
        date: '2024-09-06',
        imageLinks: [
          {link: '../../../assets/feature-images/collection-task.png', route: '/collections'},
        ],
        expanded: false,
        category: 'platform',
        order: 2
      },
      {
        id: 3,
        title: 'Value Added Data Products: Elevation Products, Super Resolution and Indices',
        description: 'When tasking, multiple value-added data products (VADPs) can now be ordered.',
        what:'When tasking, users can order multiple value-added data products such as elevation models, super-resolution and indices.',
        why:'Value-added data products enhance the use of Earth observation data. Today, users experience significant friction and time lag for these products, produced internally or through their third-party service provider.',
        how: `Users can add one or more VADPs from the cost section of the new task screen to their tasking workflow. When added, the cost of the products is calculated and made available on the dashboard and further in the payment
         screen with their detailed breakdown, along with the sensor cost. The cost calculation varies based on either sqkm or task. For example, an elevation product is based on sqkm, whereas indices are charged for each delivery.
         Based on their service level agreements, the VADPs are delivered soon after the data is delivered.
         
         It is important to note that users must actively release the data for VADPs after they are happy with the data delivered. If they no longer want the VADPs, they could ask for a refund of the price paid for the VADP. 
         The products can also be returned for credit if they do not meet the requisite quality.`,
        date: '2024-10-07',
        imageLinks: [
          {link: '../../../assets/feature-images/vadp.png', route: '/newtask'},
        ],
        expanded: false,
        category: 'platform',
        order: 3
      },
      {
        id: 4,
        title: 'New Tasking Strategy: Help Select Sensors',
        description: `Guiding users to a select set of sensors based on their use case.`,
        what:'A tasking workflow to narrow down the selection of sensors for a given use case.',
        why:'The platform has several sensors, which can overwhelm some users.',
        how:'The platform shows a curated set of pre-defined use cases as users provide their purposes. When a use case is selected, the recommended sensors are down-selected.',
        date: '2024-10-17',
        imageLinks: [
          {link: '../../../assets/feature-images/help-select.png', route: '/newtask'}
        ],
        expanded: false,
        category: 'platform',
        order: 4
      },
      {
        id: 5,
        title: 'Sensor updates',
        description: 'The platform now includes the following sensors:',
        what: `Users can look back into their past AIS refreshes within the area and time of interest.`,
        why:`Revisiting past refreshes is a requirement to understand the steps taken to detect vessel(s) of interest.`,
        how: `Each AIS feed refresh within the area of interest triggers the saving of the location GeoJSON, which then becomes available for map display and download.`,
        date: '2024-11-02',
        imageLinks: [
        ],
        expanded: false,
        category: 'sensors',
        sensorDetails: [
          {
            sensorType: 'Hyperspectral Imaging (HSI)',
            resolution: '5.3m',
            numberOfBands: 23,
            availableBands: ['G (503-570nm)', 'Y (585-615nm)', 'R (635-700nm)', 'RE (712-800nm)'],
            taskingTiers: ['Priority ', 'Standard'],
            screenshot: '../../../assets/feature-images/hsi.png',
            route: '/newtask',
            expanded: false
          },
          {
            sensorType: 'Multispectral Imaging (MSI)',
            resolution: '0.5m PAN / 2m MSI',
            numberOfBands: 5,
            availableBands: ['R', 'G', 'B', 'NIR', 'PAN'],
            taskingTiers: ['Rush', 'Priority ', 'Standard'],
            screenshot: '../../../assets/feature-images/msi-gok.png',
            route: '/newtask',
            expanded: false
          },
          {
            sensorType: 'Stereo-2D/3D',
            resolution: '0.5m PAN / 2m MSI',
            numberOfBands: 5,
            availableBands: ['R', 'G', 'B', 'NIR', 'PAN'],
            taskingTiers: ['Rush', 'Priority ', 'Standard'],
            screenshot: '../../../assets/feature-images/stereo-gok.png',
            route: '/newtask',
            expanded: false
          },
          {
            sensorType: 'C-band SAR',
            resolution: '1 to 20m (depending on the imaging mode)',
            numberOfModes: 4,
            modes: ['Spotlight', 'StripMap', 'Narrow ScanSAR', 'Extra ScanSAR '],
            frequency: 'C-band',
            polarisation: 'VV',
            taskingTiers: ['Rush', 'Priority ', 'Standard'],
            screenshot: '../../../assets/feature-images/c-sar.png',
            route: '/newtask',
            expanded: false
          },
        ],
        order: 5
      },
      {
        id: 6,
        title: 'AIS refresh history',
        description: 'You can recall and visualise refreshed AIS.',
        what: `Users can look back into their past AIS refreshes within the area and time of interest.`,
        why:`Revisiting past refreshes is a requirement to understand the steps taken to detect vessel(s) of interest.`,
        how: `Each AIS feed refresh within the area of interest triggers the saving of the location GeoJSON, which then becomes available for map display and download.`,
        date: '2024-11-25',
        imageLinks: [
          {link: '../../../assets/feature-images/ais-refresh.png', route: '/collections'},        
        ],
        expanded: false,
        category: 'platform',
        order: 6
      },
      {
        id: 7,
        title: 'Flood inundation',
        description: 'SAR sensor tasking results in flood inundation information.',
        what: `Users can receive flood extent information when tasking over floods.`,
        why:`Turning SAR data into accurately identifying flood inundation is laborious. We have made it easy by automatically extracting the information.`,
        how: `Users can request a flood inundation feed like any other feed when tasking any SAR sensor on the platform. The platform delivers a series of polygons as a GeoJson when extracting the flood map from the SAR data. The information delivered automatically removes any existing water bodies.`,
        date: '2024-12-27',
        imageLinks: [
          {link: '../../../assets/feature-images/flood-detection.png', route: '/collections'},        
        ],
        expanded: false,
        category: 'platform',
        order: 7
      },
      {
        id: 8,
        title: 'Multi-user management',
        description: 'The registered user on the platform can create additional users for tasking access to the platform.',
        what: `The registered user can create additional users without sharing his/her email address and eliminating the need to create a generic email for group sharing.`,
        why:`Organisations have multiple tasking users, and email sharing does not identify the tasker. The same email may not be used to complete simultaneous tasks if different individuals share it. Besides, organisational users may have different tasking requirements that must be tracked separately.`,
        how: `The primary user has access to user management under the users menu on the left menu panel. The registered user can create additional users, and all newly created users, including the registered user, will now share the account and any balance attached to it. The account is now at the organisational level. All tasks will continue to generate invoices, receipts, and credit notes regardless of who tasked within the organisation.
        Only the registered user can deposit money into the account and generate a quotation if required before doing so. Only the registered user can add additional users within the organisation.`,
        date: '2025-01-28',
        imageLinks: [
          {link: '../../../assets/feature-images/multiuser.png', route: '/user-manangement'},        
        ],
        expanded: false,
        category: 'platform',
        order: 8
      },
      {
        id: 9,
        title: 'Access platform without deposit',
        description: 'Access to the Eartheye tasking and feeds platform is now available without a minimum deposit.',
        what: `Customers can now register to access a limited number of sensors and platform capabilities without any deposit. Six sensors are available via the pay-per-task, no-deposit method using payment gateways.
        These sensors are:`,
        itemList: [
          {
            name: '2, sub-meter optical sensors'
          },
          {
            name: '2, sub-meter SAR sensors'
          },
          {
            name: '1, over a meter optical sensor'
          },
          {
            name: '1, 2-min refresh AIS sensor'
          },
        ],
        why:`We enabled the no-deposit access method to make earth observation data widely accessible. This method also serves customers who want to “dip their toes in the water” before using the platform fully.`,
        how: `When registering on the platform, customers can choose between with and without deposit options. 
        Regardless of the choice made to join the platform, a know-your-customer (KYC) verification process will follow. 
        Once approved, a secure link will be sent to complete the registration. When signing up as no-deposit customers, limited platform capabilities such as a single workflow will also be enabled, in addition to the limited number of sensors.  `,
        date: '2025-02-03',
        imageLinks: [
          {link: '../../../assets/feature-images/ndc.png', route: '/register'},        
        ],
        expanded: false,
        category: 'platform',
        order: 9
      },
      {
        id: 10,
        title: 'Information Feeds Refund',
        description: 'Feeds can be returned if they do not meet the accuracy metric.',
        what: `Feeds are information extracted from tasked data. We build one model per feed that is expected to perform with the same accuracy on multiple sensors. 
        This is challenging, and it will take repeated and multi-sensor training to achieve the same accuracy across all the sensors universally.`,
        why:`Every model delivering a feed is shown an accuracy score when attaching the feed to a task. This sets a confident expectation. When this expectation is not met, a refund is possible.`,
        how: `Customers can return a feed ordered from the collection page and have the cost of the feed refunded. Customers will justify the return. Once a feed is returned, it should not be used.`,
        date: '2025-02-10',
        imageLinks: [
          {link: '../../../assets/feature-images/feed-refund.png', route: '/collections'},        
        ],
        expanded: false,
        category: 'platform',
        order: 10
      },  
      {
        id: 11,
        title: 'Value Added Data Products Refund',
        description: 'Value-added data products (VADP) can be returned if they do not meet the expected quality.',
        what: `VADP includes elevation products, super-resolution products, and indices. These products are source-dependent. At times, some of these products may not meet the required usability, 
        which may be due to processing or the quality of the data. If so, they can be returned for a full refund.`,
        why:`Providing a VADP with a return option gives customers confidence in attaching them to their tasking workflow. 
        These products are meant to improve the usability of satellite data by removing all friction associated with processing, including specialised software and a higher level of specialised technical skills.`,
        how: `Customers can return a VADP ordered from the collection page and have the product cost refunded. Customers will justify the return. Once a VADP is returned, it should not be used.`,
        date: '2025-02-14',
        imageLinks: [
          {link: '../../../assets/feature-images/vadp-refund.png', route: '/collections'},        
        ],
        expanded: false,
        category: 'platform',
        order: 11
      },  
      {
        id: 12,
        title: 'Sensor preview',
        description: 'Customers can see all the sensors on the platform before starting a tasking project.',
        what: `Previously, customers only saw the available sensors on the platform after they set up the project. This was inconvenient when a quick preview of available sensors was required.`,
        why:`A customer pointed to the friction the previous design had created. We created the sensor preview option right at the beginning of the new task page. `,
        how: `Customers can now click on the sensor preview option to see all the sensors available on the platform. 
        The sensors that will eventually be validated based on the area or point of interest (AOI/POI), time of interest (TOI), 
        and frequencies will continue to be shown in the sensor cost section of the new task page.`,
        date: '2025-03-11',
        imageLinks: [
          {link: '../../../assets/feature-images/sensor-preview.png', route: '/newtask'},        
        ],
        expanded: false,
        category: 'platform',
        order: 12
      },
      {
        id: 13,
        title: 'Additional bands from Operator 50',
        description: 'Additional bands in the very near-infrared (VNIR) hyperspectral sensor.',
        what: `A Hyperspectral operator introduced additional bands in the very near-infrared (VNIR), increasing the number from 23 to 31 bands.`,
        why:`More bands in narrower wavelengths help to identify more details.`,
        how: `There are now two product options: one with 23 bands and the other with 31 bands, with no price difference.`,
        date: '2025-03-15',
        imageLinks: [
          {link: '../../../assets/feature-images/wyvern.jpg', route: '/newtask'},        
        ],
        expanded: false,
        category: 'sensors',
        order: 13
      },
      {
        id: 14,
        title: 'Changes to Operator 15',
        description: 'Several changes in tasking tiers, minimum capture area, new product (L1D) and pricing.',
        what: ``,
        itemList: [
          {
            name: 'The operator now offers two tasking tiers – Standard and Rush – instead of the four previously.'
          },
          {
            name: 'Point of interest (POI) and Area of interest (AOI) options are available over land, and POI over open waters.'
          },
          {
            name: 'The minimum capture area for POI is 25sqkm'
          },
          {
            name: 'A new product—L1D, which is orthorectified—is only available over land captures and delivered about 10 hours after L1B (the default product) on request from the collection page.'
          },
          {
            name: 'Please see new prices in the cost section of the new task screen.'
          }
        ],
        why:`The operator has streamlined their products and delivery.`,
        how: `No change to the tasking experience. Request L1D over land from the collection page when notified of its availability.`,
        date: '2025-03-20',
        imageLinks: [
          {link: '../../../assets/feature-images/nusat.jpg', route: '/newtask'},        
        ],
        expanded: false,
        category: 'sensors',
        order: 14
      },
      {
        id: 15,
        title: 'New hyperspectral imaging (HSI) sensor: Operator 52',
        description: '150 bands HSI sensor on the platform now',
        what: `A new HSI sensor with 150 bands in the very near infrared (VNIR) wavelengths between 469.999nm and 891.002nm is now available in 10m resolution.`,
        why:`More bands in the narrow wavelengths mean more objects and features on Earth can be differentiated.`,
        how: ` Task the sensor in either 50 or 95 bands combination in 7 windows. The information provided alongside the sensor
         in the cost section of the new task screen includes the Signal-to-Noise Ratio (SNR) information.`,
        date: '2025-04-07',
        imageLinks: [
          {link: '../../../assets/feature-images/loft.jpg', route: '/newtask'},        
        ],
        expanded: false,
        category: 'sensors',
        order: 15
      },
      {
        id: 16,
        title: 'User preference settings for tasking tiers',
        description: 'Users can now set their preference for tasking tier display',
        what: `Tasking tiers were previously shown in the drop-down menu as being from fastest on top to slowest at the bottom.`,
        why:`Some customers wanted the other way around.`,
        how: `The tasking tier column has a user preference setting to satisfy everyone. Users can now select the order in which they see the tasking tiers from the drop-down menu.`,
        date: '2025-04-09',
        imageLinks: [
          {link: '../../../assets/feature-images/tasking-tier.jpg', route: '/newtask'},        
        ],
        expanded: false,
        category: 'platform',
        order: 16
      },
      {
        id: 17,
        title: 'AIS-based vessel identification and filters for easy track and trace of vessel(s) of interest',
        description: 'AIS data can now be visualised by fifteen different vessel types and filtered with several attributes of interest.',
        what: `AIS data can be visualised with color codes and filtered in the area of interest with the following filters: vessel length, vessel speed, vessel name, vessel type
        IMO number, MMSI, country of registration, destination. One or more can be used to filter based on the need.`,
        why:`Presented with tens and hundreds of vessels in the area of interest, identifying the vessel of interest is like looking for a needle in a haystack. Filters get to the outcome faster.`,
        how: `When vessels appear in the area of interest at the Collections page, they will do so with different color codes. Use the filter icon, as shown, to filter results based on requirements.`,
        date: '2025-04-15',
        imageLinks: [
          {link: '../../../assets/feature-images/vessel-filter.png', route: '/collections'},        
        ],
        expanded: false,
        category: 'platform',
        order: 17
      },
      {
        id: 18,
        title: 'Highlight each sensor’s coverage capability',
        description: 'Removing friction associated with knowing each sensor’s coverage capability.',
        what: `Not all sensors can do point of interest (POI) tasking and area of interest (AOI) tasking. Some can do only one of them.`,
        why:`Presenting this information upfront helps to decide which sensor to use based on the coverage requirement.`,
        how: `The coverage capability is shown via an icon and a tooltip, both in the sensor preview screen and later in the sensor-cost details.`,
        date: '2025-04-25',
        imageLinks: [
          {link: '../../../assets/feature-images/target.png', route: '/newtask'},        
        ],
        expanded: false,
        category: 'platform',
        order: 18
      },
      {
        id: 19,
        title: 'Time display setting on the dashboard',
        description: 'Ability to switch between UTC and registered country time (local time).',
        what: `The platform operates in UTC. Users enter their time of interest (TOI) in their registered country's time, also known as local time, in the new task screen when setting up a task.
         The platform automatically converts to UTC and displays the time in UTC.`,
        why:`The dashboard continued to display UTC while users had entered their TOI in the local time, which was confusing some users. Others wanted it to be left in UTC. We needed to find a solution that benefits both parties.`,
        how: `As shown, the time display can now be based on user settings in the dashboard. User can choose between their registered country's time or UTC to display all their tasks in the dashboard.`,
        date: '2025-04-29',
        imageLinks: [
          {link: '../../../assets/feature-images/time-converter.png', route: '/task'},        
        ],
        expanded: false,
        category: 'platform',
        order: 19
      },
      {
        id: 20,
        title: 'Enable or disable users by superusers',
        description: 'Extending multi-user capability with additional controls.',
        what: `A Super user can enable or disable a user from partaking in platform activities. The user can continue to sign on to the platform, but will not be able to perform any functions.`,
        why:`Organisations may need to temporarily suspend users without entirely revoking access to the platform for a while.`,
        how: `Super users can use the relevant checkbox, as shown, in the user management screen to enable/ disable a user. If there are any ongoing tasks by the users, the control will not work.`,
        date: '2025-06-07',
        imageLinks: [
          {link: '../../../assets/feature-images/user-enable.png', route: '/user-manangement'},        
        ],
        expanded: false,
        category: 'platform',
        order: 20
      },
      {
        id: 21,
        title: 'Geofencing taskable areas',
        description: 'Extending multi-user capability with additional controls.',
        what: `Users can be assigned to their tasking areas.`,
        why:`Assignment prevents users from unauthorised tasking based on organisational operational requirements.`,
        how: `Superuser can assign one or more polygons by uploading, as shown, in the user management screen for each user. No assignment means a user could task globally.`,
        date: '2025-06-07',
        imageLinks: [
          {link: '../../../assets/feature-images/geofencing.png', route: '/user-manangement'},        
        ],
        expanded: false,
        category: 'platform',
        order: 21
      },
      {
        id: 22,
        title: 'Permissions-based tasking management',
        description: 'In a multi-user organisation, users can be assigned tasking roles.',
        what: `Once an organisation is set as permission-based, as shown, users can be assigned one or more specific roles in a tasking workflow. The following permissions are available to be set for each user:`,
          itemList: [
          {
            name: 'Create or Cancel Task/Feeds'
          },
          {
            name: 'Approve or Reject Task/Feeds'
          },
          {
            name: 'Order Task/Feeds'
          },
          {
            name: 'Cancel Ordered Task/Feeds'
          },
          {
            name: 'Receive Data'
          },
          {
            name: 'Collection Point Tasking'
          },
        ],
        why:`Organisations want specific users to play particular roles. For example, only certain users can place an order, an action that necessitates payment for the task.`,
        how: `The permissions options are granular, as described, down to each activity in a tasking workflow, including who can receive the tasked data. 
        The superuser in the organisation can assign, as shown, one more permission to the selected user. No assignment under a permission-based organisation 
        setting would mean a user not assigned any permission is unable to perform any tasks. 
        When a task progresses, the platform notifies users assigned relevant permissions to partake in the workflow. Superusers have access to all permissions automatically. 
        Therefore, when a task does not have any users assigned to progress to the next stage, the superuser is notified by default.`,
        date: '2025-06-07',
        imageLinks: [
          {link: '../../../assets/feature-images/permission.png', route: '/user-manangement'},        
        ],
        expanded: false,
        category: 'platform',
        order: 22
      },
      {
        id: 23,
        title: 'Satellite TLEs in real-time',
        description: 'Displaying satellites in their current positions and predicted positions.',
        what: `Users can now see satellite positions as they choose sensors or see predicted positions when they select the time of interest (TOI) for the task.`,
        why:`Knowing the satellites' locations is helpful to make a choice of operator(s) or the sensor(s) for the planned tasking.`,
        how: `Users can see the present positions of the satellites as they choose each operator in the preview screen, as shown, 
        by activating the satellite icon. Furthermore, they can see the predicted positions, as shown, before the start of their TOIs up to three days before the TOI start.`,
        date: '2025-06-15',
        imageLinks: [
          {link: '../../../assets/feature-images/tle.png', route: '/newtask'},        
        ],
        expanded: false,
        category: 'platform',
        order: 23
      },
      {
        id: 24,
        title: 'Multiple use scenarios under intent',
        description: 'Identifying the correct licensing based on the proposed data use.',
        what: `Identifying appropriate End User License Agreement (EULA) to support the proposed use of the tasked data is now streamlined under seven intent types, as listed:`,
        itemList: [
          {
            name: 'Internal Use'
          },
          {
            name: 'Product for distribution (derived works)'
          },
          {
            name: 'Product for distribution (derived works with attribution) '
          },
          {
            name: 'Publish publicly (derived works)'
          },
          {
            name: 'Publish publicly (1024x1024 pixels with attribution)'
          },
          {
            name: 'Publish publicly (with attribution)'
          },
          {
            name: 'Internal Use + Product For Distribution'
          },
        ],
        why:`Use of data incorrectly runs the risk of EULA violation and litigation by the operators on the platform.`,
        how: `On selecting the intent from the drop-down from the tasking details section in the new task screen, as shown, 
        the sensor data conforming to the chosen intent are alone listed in the sensor-cost details section. The same filtering is applied when users task 
        with outcome-based tasking, where, once again, intent selection is made.`,
        date: '2025-06-17',
        imageLinks: [
          {link: '../../../assets/feature-images/intent.png', route: '/newtask'},        
        ],
        expanded: false,
        category: 'platform',
        order: 24
      },
      {
        id: 25,
        title: 'New, very high-resolution data',
        description: '15cm and 25cm optical data for the first time on the platform',
        what: `New high definition (HD) multi-spectral data in 15cm and 25cm from 30cm ground sample distance (GSD) and 50cm GSD, 
        respectively, are now available on the platform. The data comes with three bands of RGB as an L4 (orthorectified) product.`,
        why:`From defence and intelligence to construction use cases require very high-resolution, affordable imagery on demand.`,
        how: `Task Operator 3 and Operator 4 sensors for the respective resolutions for the L4 product, as shown.`,
        date: '2025-06-19',
        imageLinks: [
          {link: '../../../assets/feature-images/high_res_optical.png', route: '/newtask'},        
        ],
        expanded: false,
        category: 'sensors',
        order: 25
      },
      {
        id: 26,
        title: 'New emergency tasking tier',
        description: 'Fastest delivery of 50cm and 70cm optical/ multispectral data',
        what: `Operator 15 data is now delivered faster than before under the emergency tasking. The data is available within three hours of collection.`,
        why:`Low latency of high-resolution data is required for situational awareness.`,
        how: `Select operator 10 and choose the Emergency tasking tier. As shown, the response time is less than or equal to three hours.`,
        date: '2025-06-21',
        imageLinks: [
          {link: '../../../assets/feature-images/emergency-tier.png', route: '/newtask'},        
        ],
        expanded: false,
        category: 'sensors',
        order: 26
      },
      {
        id: 27,
        title: 'New equatorial SAR sensor',
        description: 'Another high revisit equatorial SAR is now live.',
        what: `A new SAR sensor, Operator 35, is now on the platform. The sensor is a high-to medium-resolution X-band SAR operating in a near-equatorial orbit with a latitude range of ±13 °.`,
        why:` Equatorial regions are prone to cloud cover and geopolitically challenged. Situational awareness requires high revisit. With up to four revisits within 24 hours at any location   
          within the specified latitude range, the sensor is another option that can be combined with other SSO SAR sensors and with those with mid-inclined angles, increasing revisit and coverage.`,
        how: `As shown, choose Operator 35 for 1-meter (SPOT) or 3-meter (STRIP) resolution cum modes.`,
        date: '2025-06-25',
        imageLinks: [
          {link: '../../../assets/feature-images/equatorial-sar.png', route: '/newtask'},        
        ],
        expanded: false,
        category: 'sensors',
        order: 27
      },
      {
        id: 28,
        title: 'New C-band SAR sensor',
        description: 'A C-band SAR sensor is now live.',
        what: `A new SAR sensor, Operator 31, is now on the platform. This is the first C-band SAR on the EARTHEYE platform.`,
        why:`C-band SAR is well-suited for maritime use cases besides plantation and agriculture.`,
        how: ` As shown, choose Operator 31 with the various modes for their corresponding resolutions.`,
        date: '2025-07-02',
        imageLinks: [
          {link: '../../../assets/feature-images/cbandSar.png', route: '/newtask'},        
        ],
        expanded: false,
        category: 'sensors',
        order: 28
      },
      {
        id: 29,
        title: 'Task AOI on the map',
        description: 'Area tasking directly from the map search.',
        what: `Users can enter a closed polygon in the map search bar to create the area of   
interest (AOI) instead of drawing it.`,
        why:` Some users have found it easy to enter pairs of coordinates as a closed polygon for   
tasking.`,
        how: ` Users enter a lng,lat pair, separated by a semicolon, as shown, to create the AOI for   
the tasking request.`,
        date: '2025-07-08',
        imageLinks: [
          {link: '../../../assets/feature-images/mapSearch.png', route: '/newtask'},        
        ],
        expanded: false,
        category: 'platform',
        order: 29
      },
      {
        id: 30,
        title: 'Sub-budget allocation',
        description: 'Allocate organisational budget to individual users',
        what: `Users can now be allocated budgets aggregating to the organisational budget.`,
        why:` Organisations want to empower users with budget allocation for tasking. The usage is restricted to the allocation. `,
        how: ` The super user can allocate budgets to users by selecting each user and setting the   
budget as shown below.`,
        date: '2025-07-16',
        imageLinks: [
          {link: '../../../assets/feature-images/subBudget.png', route: '/profile'},        
        ],
        expanded: false,
        category: 'platform',
        order: 30
      },
      {
        id: 31,
        title: 'New L-band SAR sensor',
        description: 'A L-band SAR sensor is now live.',
        what: `A new SAR sensor, Operator 36, is now on the platform. This is the first L-band SAR on the EARTHEYE platform.   `,
        why:` L-band SAR is well-suited to penetrate canopy cover and reach the soil, so the soil moisture content could be accurately assessed. The use cases include determining a   
              structure's height to calculate biomass accurately.   `,
        how: ` As shown, choose Operator 36 with the various modes for their corresponding resolutions.`,
        date: '2025-07-21',
        imageLinks: [
          {link: '../../../assets/feature-images/lbandSar.png', route: '/newtask'},        
        ],
        expanded: false,
        category: 'sensors',
        order: 31
      },
      {
        id: 32,
        title: 'Resolution-specific tasking',
        description: 'Separation of native and super resolution of Operator 15',
        what: `Operator 15’s native resolution, known as Ground Sample Distance (GSD), is   
decoupled from its super-resolution.`,
        why:` Users can now consistently receive captures at the required resolution.`,
        how: `While tasking Operator 15, as shown, choose one of the three resolution options   
available.`,
        date: '2025-08-05',
        imageLinks: [
          {link: '../../../assets/feature-images/GSD.png', route: '/newtask'},     
        ],
        expanded: false,
        category: 'platform',
        order: 32
      },
      {
        id: 33,
        title: 'Multiple access plans',
        description: 'New deposit plans to access the EARTHEYE platform',
        what: `EARTHEYE access plans have been streamlined and simplified with four new plans   
introduced. These are Starter, Standard, Premium and Custom. The latter requires   
contacting sales from the website or this platform for discounts.`,
        why:` Users have more choices now based on their usage patterns and sensor   
requirements. A selection is made based on sensor type and number, as well as platform   
capabilities.`,
        how: ` When registering for access to the EARTHEYE platform, users choose between the   
four options. Users can also request a plan upgrade at any time from the profile page.   `,
        date: '2025-09-24',
        imageLinks: [
          {link: '../../../assets/feature-images/plan.png', route: '/register'},        
        ],
        expanded: false,
        category: 'platform',
        order: 33
      },
      {
        id: 34,
        title: 'New C and L-band InSAR',
        description: 'InSARs (Interferometry SAR) in C and L bands are available on the platform.',
        what: `Operators 31 and 36 providing C and L bands, respectively, are also capturing ground track-based InSAR.`,
        why:` InSAR is useful to detect minute displacements from mining sites to landslide hotspots.`,
        how: ` As shown, Operators 31 and 36 can be tasked in the InSAR mode. The InSAR capability is listed under the InSAR sensor type.`,
        date: '2025-09-03',
        imageLinks: [
          {link: '../../../assets/feature-images/insarLbandCband.png', route: '/newtask'},        
        ],
        expanded: false,
        category: 'sensors',
        order: 34
      },
      {
        id: 35,
        title: 'New PAN sensor',
        description: 'A panchromatic sensor (PAN) is now live',
        what: `A new PAN sensor, Operator 16, is now on the platform. The ~4m resolution sensor is uniquely positioned for maritime capture, given the long strip that extends for   
             thousands of kilometers.   `,
        why:` A PAN sensor with large-area coverage is helpful for maritime use cases, such as detecting vessels or transshipments.`,
        how: ` As shown, choose Operator 16 by varying the length for the desired strip.`,
        date: '2025-10-26',
        imageLinks: [
          {link: '../../../assets/feature-images/operator16.png', route: '/newtask'},        
        ],
        expanded: false,
        category: 'sensors',
        order: 35
      },
      {
        id: 36,
        title: 'Credit memo for invoice customers',
        description: 'Credit memo for pay-later customers, similar to a credit note for pay-now or a debit account customer.',
        what: `A credit memo is now issued for customers who have generated an invoice but are   
due a refund.`,
        why:` Just like a credit note is issued for having returned the funds charged via Pay Now or   
by debiting the account to the user account balance, a credit note serves the same   
purpose for a Pay Later customer.`,
        how: ` A credit memo is automatically created and posted in the Orders page along with   
credit notes.`,
        date: '2025-10-27',
        imageLinks: [
          {link: '../../../assets/feature-images/creditMemo.png', route: '/orders'},        
        ],
        expanded: false,
        category: 'platform',
        order: 36
      },
      {
        id: 37,
        title: 'Tip-and-cue with observations',
        description: 'New tasking strategy: Tip-and-cue tasking strategy with imaging and non-imaging sensors combined.',
        what: `Tip-and-cue workflows are advanced tasking strategies in which an observation   
from a tipped sensor triggers the cueing of the second sensor. For example, an AIS   
location-of-interest detection triggers automated tasking of an optical or SAR sensor.`,
        why:` Intelligence gathering requires different sensors to work together. The tasking   
strategy enables just that.`,
        how: ` A user can choose when to task a cueing sensor. Information from the tipping   
sensor is extracted as an observation, such as the location of a vessel or the number of   
vessels in an area, and the cueing sensor is automatically triggered if the condition is met.   
See the images for the tip-and-cue configuration. `,
        date: '2025-10-29',
        imageLinks: [
          {link: '../../../assets/feature-images/tipAndCue.png', route: '/newtask'},        
        ],
        expanded: false,
        category: 'platform',
        order: 37
      },
      {
        id: 38,
        title: 'New TIR sensor',
        description: 'A new thermal infrared (TIR) sensor is back again. ',
        what: `A thermal sensor from Operator 61 is now on the platform. The 30m and 69m   
resolutions sensor uniquely captures thermal images in both VNIR (Very Near Infrared)   
and LWIR (Long Wave Infrared) bands, respectively.`,
        why:` For example, TIR helps detect volcanic eruptions in LWIR and vessels with heat   
emissions in VNIR.`,
        how: ` As shown, choose Operator 61 for TIR and collect both VNIR and LWIR bands within   
the wavelengths as specified on the platform.  `,
        date: '2025-11-21',
        imageLinks: [
          {link: '../../../assets/feature-images/operator61.png', route: '/newtask'},        
        ],
        expanded: false,
        category: 'sensors',
        order: 38
      },
      {
        id: 39,
        title: 'Budgeting streamlined',
        description: 'Budgeting is separated into imaging and non-imaging',
        what: ` Users can now request a budget for a proposed task, including imaging, non-imaging, or both.`,
        why:` Separation enables flexible input of user requirements.`,
        how: ` Users click the budget menu option in the left panel of the dashboard, as shown, to enter inputs for budget generation. When a budget is generated,
         users can either task straightaway, providing additional information where required, or save for later reference or tasking if the time of interest (TOI) has not expired. `,
        date: '2025-11-23',
        imageLinks: [
          {link: '../../../assets/feature-images/nonImaging.png', route: '/newbudget'},        
        ],
        expanded: false,
        category: 'platform',
        order: 39
      },
      // {
      //   id: 40,
      //   title: 'New HSI sensor',
      //   description: ' A new hyperspectral imaging (HSI) sensor is on the platform.',
      //   what: ` A HSI sensor from Operator 54 is now on the platform. The 5.3m resolutions sensor   
      //     uniquely captures 150 spectral bands from 472.7nm to 888.5nm wavelength range.`,
      //   why:` HSI with a narrower spectral band is ideal for spectral signatures used in detection.   
      //     The applications range from disease detection in plantations and materials used in vessels   
      //     to water quality assessment after floods.`,
      //   how: ` As shown, Operator 54 provides tasking across multiple user-selectable band   
      //     windows. Alternatively, the sensor can also be tasked based on use cases.  `,
      //   date: '2025-11-28',
      //   imageLinks: [
      //     {link: '../../../assets/feature-images/operator54.png', route: '/newtask'},
      //     {link: '../../../assets/feature-images/usecase.png', route: '/newtask'},
      //   ],
      //   expanded: false,
      //   category: 'sensors',
      //   order: 40
      // },
      // {
      //   id: 41,
      //   title: 'Vessel location prediction',
      //   description: 'Dark vessel location prediction',
      //   what: `When a vessel's AIS (Automatic Identification System) is switched off, users can request a prediction of the following five locations.`,
      //   why:` Looking for a vessel that has gone dark is not a trivial issue. Different sensor   
      //     modalities can be used to locate the vessel, but determining where to task them once it   
      //     goes dark is challenging. Hence, EARTHEYE predicts the following five locations that the   
      //     vessel might be at in the same intervals as the history of its observations.`,
      //   how: ` As shown, a user can request future predictions from the vessel's last known   
      //     location. Using a Machine Learning model, the platform plots the predicted locations with   
      //     their directions.`,
      //   date: '2025-12-10',
      //   imageLinks: [
      //     {link: '../../../assets/feature-images/prediction.png', route: '/collections'},        
      //   ],
      //   expanded: false,
      //   category: 'platform',
      //   order: 41
      // },
      // {
      //   id: 42,
      //   title: 'Multiple information extracted from the tasked data',
      //   description: 'Multiple feeds can be attached to a task',
      //   what: `Previously, only a single feed could be attached to a task with one scene from each   
      //         of the multiple sensors in the task. This is now changed to accommodate the following:`,
      //          itemList: [
      //         {
      //           name: 'a single feed can be generated for multiple scenes of a sensor or sensors'
      //         },
      //         {
      //           name: 'multiple feeds can be attached to multiple scenes of a sensor or sensors.'
      //         }
      //       ],
      //   why:` The multiple feeds capability enables more information and intelligence gathering from an image or set of images.
      //       For example, it is now possible to overlay vessel- and oil-spill-detection feeds onto a maritime image.`,
      //   how: ` As shown, the sensor-cost section of the new task screen displays the feeds available   
      //       for each sensor. These feeds can then be attached to the task upon task approval, before   
      //       placing the task order on the dashboard.`,
      //   date: '2025-12-10',
      //   imageLinks: [
      //     {link: '../../../assets/feature-images/multiFeeds.png', route: '/newtask'},        
      //   ],
      //   expanded: false,
      //   category: 'platform',
      //   order: 42
      // },
    ];

    this.filteredFeatures = [...this.features.reverse()];
  }

  ngOnInit(): void {
    document.body.style.overflow = 'hidden';
  }
  ngOnDestroy() {
    document.body.style.overflow = 'auto';
  }

  ngAfterViewInit() {
    // Listen for scroll events on the timeline container
    this.timelineContainer.nativeElement.addEventListener('scroll', this.onScroll.bind(this));
  }

  onScroll(): void {
    const container = this.timelineContainer.nativeElement;
    const scrollPosition = container.scrollTop;
    const timelineItems = container.querySelectorAll('.update-item');

    timelineItems.forEach((item: any, index: number) => {
      const itemOffsetTop = item.offsetTop;
      const itemOffsetBottom = itemOffsetTop + item.clientHeight;

      // Adjust the scrollPosition based on container's height
      if (scrollPosition + container.clientHeight / 2 > itemOffsetTop && scrollPosition < itemOffsetBottom) {
        this.activeIndex = index;
      }
    });
  }

  // Function to toggle the expand/collapse state for the specific feature
  toggleExpand(index: number) {
    this.filteredFeatures[index].expanded = !this.filteredFeatures[index].expanded;
  }

  toggleSensor(featureIndex: number, sensorIndex: number) {
    const feature = this.filteredFeatures[featureIndex];
    if (feature && feature.sensorDetails) {
      feature.sensorDetails[sensorIndex].expanded = !feature.sensorDetails[sensorIndex].expanded;
    }
  }

  selectCategory(index: number,category: any) {
    this.selectedIndex = index;
    // Filter features based on the selected category
    if (category.featureKey === 'all') {
      this.filteredFeatures = [...this.features]; // Show all features
    } else {
      this.filteredFeatures = this.features.filter(feature => feature.category === category.featureKey);
    }
  }

}
